/** @jsxImportSource preact */
import type { FunctionComponent, JSX, RefObject, VNode } from "preact"
import type { Dispatch } from "preact/hooks"
import "#js/util/temporal-global"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "preact/hooks"
import {
  attachmentSourceForTool,
  markAttachmentSource,
} from "#js/pure/attachment-source"
import type {
  UpfileAction,
  UpfileControlState,
  UpfileStateFlags,
  UpfileUiHintFlags,
} from "#js/pure/upfile"
import {
  getShownControls,
  toUpfileStateFlags,
  toUpfileUiHintFlags,
} from "#js/pure/upfile"
import {
  createRoot,
  type EventNode,
  type TypedEvent,
} from "#js/util/component-hierarchy"
import { nowEpochMilliseconds } from "#js/util/time"
import {
  PreactComponentScope,
  usePreactComponentNode,
  usePreactMediatorSnapshot,
} from "./component-hierarchy"
import {
  abortPaintPopups,
  isPaintPopupAvailable,
  type OekakiPaintPopupConfig,
  resolvePaintPopup,
} from "./oekaki-paint-popup"
import type { OekakiTool } from "./types"
import {
  type ClipboardPasteFailure,
  createUpfileUiMediator,
  isUpfileUiEvent,
  type UpfileUiEffect,
  type UpfileUiEvent,
  type UpfileUiInputEvent,
} from "./upfile-ui-mediator"

export const ACCEPTED_ATTACHMENT_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const

const FILE_PICKER_ACCEPT_TYPES = [
  "image/*",
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const
const DRAW_ONLY_ATTACHMENT_MIME_TYPES = ["image/png", "image/webp"] as const

export type { ClipboardPasteFailure } from "./upfile-ui-mediator"

export type ClipboardPasteResult =
  | { status: "success"; image: Blob }
  | { status: ClipboardPasteFailure }

const ACCEPTED_ATTACHMENT_MIME_TYPE_SET: Set<string> = new Set(
  ACCEPTED_ATTACHMENT_MIME_TYPES,
)
const DRAW_ONLY_ATTACHMENT_MIME_TYPE_SET: Set<string> = new Set(
  DRAW_ONLY_ATTACHMENT_MIME_TYPES,
)

/**
 * `upfile-input-v2`要素に外から叩き込むコマンド群。
 * fragment側が`bindCommands`で実装を差し込み、element側がhost methodとして公開する。
 */
export interface UpfileV2Commands {
  /** 隠し`<input type=file>`をクリックしてOSのファイルピッカーを開く */
  clickFileattach(): void
  /** お絵描きボタンが押されたのと等価 (アクノスペイントを起動) */
  clickPaint(): void
  /** Klecksを起動 */
  clickKlecks(): void
  /** 貼付ボタンが押されたのと等価 (クリップボードから画像を取り込む) */
  clickPaste(): void
  /** クリアボタンが押されたのと等価 (emptyモードに戻す) */
  clickClear(): void
}

/** v2添付File欄の動作に必要な設定 */
export type UpfileInputV2Props = {
  componentNode?: EventNode<TypedEvent>
  /** 添付File欄が所属するフォーム要素 */
  form: HTMLFormElement
  /** 画像添付を許可するならtrue、お絵描きのみならfalse */
  allowImageReplies: boolean
  /** はっちゃんキャンバス幅 */
  canvasWidth: number
  /** はっちゃんキャンバス高さ */
  canvasHeight: number
  /** 状態変化時に呼ばれる (element側が`aimg:upfile-state`発火に使う) */
  onStateChange?: (flags: UpfileStateFlags) => void
  /** UI推奨フラグが変わった時に呼ばれる (element側が`aimg:upfile-ui-hint`発火に使う) */
  onUiHintChange?: (hint: UpfileUiHintFlags) => void
  /** マウント時、外から叩くためのコマンド実装を受け取るコールバック */
  bindCommands?: (cmds: UpfileV2Commands) => void
}

/**
 * v2添付File欄の中身。UIボタン (🎨お絵かき / 📋貼付 / 🗑クリア / 画像添付不可label /
 * 可視fileinput) は**描画しない**。外部ツールバーが`aimg:upfile-ui-hint`を購読して
 * 自前で描画する想定。
 *
 * 保持するDOMは:
 * - 隠し`<input type="file" name="upfile">` (formに値を持たせるため)
 * - はっちゃん用`<button id=oebtnj>` / `<input id=baseform>` / `<figure id=ftbl>`+`<canvas id=oejs>`
 */
export const makeUpfileInputFragmentV2 = (
  paintPopups: OekakiPaintPopupConfig,
): FunctionComponent<UpfileInputV2Props> => {
  function UpfileInputV2Presenter(props: UpfileInputV2Props): VNode {
    const allowImageRepliesRef = useRef(props.allowImageReplies)
    allowImageRepliesRef.current = props.allowImageReplies
    const effectRunnerRef = useRef<(effect: UpfileUiEffect) => void>(
      () => undefined,
    )
    // biome-ignore lint/correctness/useExhaustiveDependencies: fragment factoryの設定と初期allow値はmount後に固定される
    const mediator = useMemo(
      () =>
        createUpfileUiMediator({
          initialAllowImageReplies: props.allowImageReplies,
          getSelectedPaintTool: () =>
            "axnos" in paintPopups
              ? (paintPopups.getOekakiTool?.() ?? "axnos")
              : "axnos",
          isPaintPopupAvailable: (tool: OekakiTool) =>
            isPaintPopupAvailable(paintPopups, tool),
          isAcceptedImage: (image: Blob) =>
            isAcceptedAttachmentMimeType(
              image.type,
              allowImageRepliesRef.current,
            ),
          runEffect: (effect: UpfileUiEffect): void =>
            effectRunnerRef.current(effect),
        }),
      [],
    )
    const handler = useCallback(
      (event: TypedEvent): "handled" | "unhandled" => {
        if (!isUpfileUiEvent(event)) return "unhandled"
        return mediator.dispatch(event).status === "handled"
          ? "handled"
          : "unhandled"
      },
      [mediator],
    )
    const node = usePreactComponentNode(handler)
    const dispatchUiEvent = useCallback(
      (event: UpfileUiEvent): void => {
        node.dispatch(event)
      },
      [node],
    )
    const uiState = usePreactMediatorSnapshot(mediator)
    const mode = uiState.mode
    const controls = useMemo(() => getShownControls(mode), [mode])
    const upfileRef = useRef<HTMLInputElement>(null)
    const baseformRef = useRef<HTMLInputElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const { previewBlob, isPopupFormCollapsed, pasteFailure } = uiState
    const setPreviewBlob = useCallback(
      (value: File | null): void => {
        dispatchUiEvent({ type: "upfile.preview.changed", value })
      },
      [dispatchUiEvent],
    )
    const setIsPopupFormCollapsed = useCallback(
      (value: boolean): void => {
        dispatchUiEvent({ type: "upfile.popup-collapse.changed", value })
      },
      [dispatchUiEvent],
    )
    const setPasteFailure = useCallback(
      (value: ClipboardPasteFailure | null): void => {
        dispatchUiEvent({ type: "upfile.paste-failure.changed", value })
      },
      [dispatchUiEvent],
    )
    const pasteAttemptGenerationRef = useRef(0)

    // biome-ignore lint/correctness/useExhaustiveDependencies: listen系は内部closureだが本体はrefs/safeなclosureしか触らないので、props.formの変化時のみ再subscribeすれば十分
    useEffect(() => listenSubmit(props.form), [props.form])
    useEffect(
      () => listenPopupFormToggled(props.form, setIsPopupFormCollapsed),
      [props.form, setIsPopupFormCollapsed],
    )
    // biome-ignore lint/correctness/useExhaustiveDependencies: unmount cleanup
    useEffect(() => (): void => abortOpenPaint(), [])
    useEffect(() => (): void => mediator.dispose(), [mediator])
    useLayoutEffect(() => {
      const initialState = mediator.getSnapshot()
      props.onStateChange?.(
        toUpfileStateFlags(initialState.mode, {
          isPopupFormCollapsed: initialState.isPopupFormCollapsed,
        }),
      )
      props.onUiHintChange?.(
        toUpfileUiHintFlags(initialState.mode, {
          allowImageReplies: props.allowImageReplies,
        }),
      )
      return mediator.subscribe((state, event) => {
        if (
          event.type === "upfile.action" ||
          event.type === "upfile.action.requested" ||
          event.type === "upfile.paint.requested" ||
          event.type === "upfile.file.received" ||
          event.type === "upfile.image.received" ||
          event.type === "upfile.popup-collapse.changed"
        ) {
          props.onStateChange?.(
            toUpfileStateFlags(state.mode, {
              isPopupFormCollapsed: state.isPopupFormCollapsed,
            }),
          )
        }
        if (
          event.type === "upfile.action" ||
          event.type === "upfile.action.requested" ||
          event.type === "upfile.paint.requested" ||
          event.type === "upfile.file.received" ||
          event.type === "upfile.image.received" ||
          event.type === "upfile.configuration.changed"
        ) {
          props.onUiHintChange?.(
            toUpfileUiHintFlags(state.mode, {
              allowImageReplies: props.allowImageReplies,
            }),
          )
        }
      })
    }, [
      mediator,
      props.allowImageReplies,
      props.onStateChange,
      props.onUiHintChange,
    ])

    useLayoutEffect(() => {
      if (
        mediator.getSnapshot().allowImageReplies !== props.allowImageReplies
      ) {
        dispatchUiEvent({
          type: "upfile.configuration.changed",
          allowImageReplies: props.allowImageReplies,
        })
      }
    }, [dispatchUiEvent, mediator, props.allowImageReplies])
    // bindCommands で渡したクロージャは"マウント時の閉包"を持つ。dispatchやcontrolsは
    // 毎レンダ作り直されるので、外から呼ばれたとき最新の値で動くようrefで遅延参照する。
    const paintPopupGenerationRef = useRef(0)
    // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot on mount (refsで最新参照)
    useLayoutEffect(() => {
      props.bindCommands?.({
        clickFileattach: () =>
          dispatchUiEvent({ type: "upfile.file-picker.requested" }),
        clickPaint: () => dispatchUiEvent({ type: "upfile.paint.requested" }),
        clickKlecks: () =>
          dispatchUiEvent({
            type: "upfile.paint.requested",
            preferredTool: "klecks",
          }),
        clickPaste: () => dispatchAction("paste-button-clicked"),
        clickClear: () => dispatchAction("clear-button-clicked"),
      })
    }, [])

    // biome-ignore lint/correctness/useExhaustiveDependencies: controlsがmode由来なのを残したい
    useEffect(() => {
      if (!props.allowImageReplies) {
        return
      }
      return listenPaste(acceptPaste)
    }, [props.allowImageReplies, controls.pasteButton, controls.upfileInput])
    // biome-ignore lint/correctness/useExhaustiveDependencies: controls.upfileInputの変化時にDrop受付を張り直す
    useEffect(() => {
      if (!controls.upfileInput) {
        return
      }
      return listenUpfileDrop(props.form, acceptDrop, props.allowImageReplies)
    }, [props.allowImageReplies, props.form, controls.upfileInput])

    // biome-ignore lint/correctness/useExhaustiveDependencies: propsの他フィールドが変わっただけで再welcomeしたくない
    useLayoutEffect(() => {
      if (controls.oejsCanvas) {
        welcomeHacchan(canvasRef.current, props)
      }
    }, [controls.oejsCanvas])

    effectRunnerRef.current = runEffect

    return (
      <PreactComponentScope node={node}>
        <UpfileInputV2View
          allowImageReplies={props.allowImageReplies}
          controls={controls}
          previewBlob={previewBlob}
          isPopupFormCollapsed={isPopupFormCollapsed}
          pasteFailure={pasteFailure}
          upfileRef={upfileRef}
          baseformRef={baseformRef}
          canvasRef={canvasRef}
          onFileSelected={acceptSelectedFile}
          onEvent={dispatchInput}
        />
      </PreactComponentScope>
    )

    /** ペーストされた画像を受け取る */
    function acceptPaste(image: Blob): void {
      dispatchUiEvent({
        type: "upfile.image.received",
        source: "paste",
        image,
      })
    }

    /** フォームへDropされた添付ファイルを受け取る */
    function acceptDrop(file: File): void {
      dispatchUiEvent({ type: "upfile.file.received", file })
    }

    /** pickerまたはDataTransferから設定されたファイルを最終検査する */
    function acceptSelectedFile(): void {
      const input = upfileRef.current
      const file = input?.files?.[0]
      if (
        !input ||
        !file ||
        !isAcceptedAttachmentMimeType(file.type, props.allowImageReplies)
      ) {
        if (input) {
          input.value = ""
        }
        console.warn("selected file ignored: unsupported MIME", file?.type)
        return
      }
      dispatchAction("file-selected")
    }

    /** 外部ツールバーと貼り付け失敗案内から、同じ既存file inputを開く */
    function openFilePicker(): void {
      upfileRef.current?.click()
    }

    /** フォーム送信関係のイベントを設定する */
    function listenSubmit(form: HTMLFormElement): () => void {
      const abort = new AbortController()
      const opts = { signal: abort.signal }

      form.addEventListener("aimg:prepare-submit", prepareSubmit, opts)

      form.addEventListener(
        "aimg:submitted",
        () => dispatchAction("submitted"),
        opts,
      )

      return () => abort.abort()
    }

    /** フォーム送信のとき使う。canvasやbaseformを変換してupfileに設定する */
    function prepareSubmit(
      e: CustomEvent<{ preparing?: Promise<void> }>,
    ): void {
      e.detail.preparing = prepareHacchanImage()?.then((image) =>
        dispatchUiEvent({ type: "upfile.submit-image.ready", image }),
      )
    }

    /** はっちゃんの出力する画像 (キャンバスorAPNG) を読み取る */
    function prepareHacchanImage(): Promise<Blob> | undefined {
      if (baseformRef.current?.value) {
        const dataUrl = `data:image/png;base64,${baseformRef.current.value}`
        return fetch(dataUrl).then((res) => res.blob())
      } else if (canvasRef.current) {
        const canvas = canvasRef.current
        return new Promise((resolve, reject) => {
          try {
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(Error("Failed to convert canvas to blob"))
                }
              },
              "image/webp",
              1,
            )
          } catch (err) {
            reject(err)
          }
        })
      }
    }

    /** ポップアップ型のお絵描きツールをクリック操作中に表示する */
    function openPaint(tool: OekakiTool): void {
      abortOpenPaint()
      const generation = paintPopupGenerationRef.current
      const { popup } = resolvePaintPopup(paintPopups, tool)

      popup
        .popup(props)
        .then((image) => {
          if (paintPopupGenerationRef.current !== generation) {
            return
          }
          dispatchUiEvent({
            type: "upfile.image.received",
            source: "paint",
            image,
          })
        })
        .catch((e) => {
          if (paintPopupGenerationRef.current !== generation) {
            return
          }
          console.warn(e)
          dispatchAction("clear-button-clicked")
        })
    }

    function abortOpenPaint(): void {
      paintPopupGenerationRef.current += 1
      abortPaintPopups(paintPopups)
    }

    function dispatchInput(event: UpfileUiInputEvent): void {
      node.dispatch(event)
    }

    function dispatchAction(action: UpfileAction): void {
      dispatchInput({ type: "upfile.action.requested", action })
    }

    function runEffect(effect: UpfileUiEffect): void {
      switch (effect.type) {
        case "abort-paint":
          abortOpenPaint()
          return
        case "cancel-paste":
          pasteAttemptGenerationRef.current += 1
          setPasteFailure(null)
          return
        case "clear-inputs":
          if (upfileRef.current) upfileRef.current.value = ""
          if (effect.clearBaseform && baseformRef.current) {
            baseformRef.current.value = ""
          }
          return
        case "clear-baseform":
          if (baseformRef.current) baseformRef.current.value = ""
          return
        case "open-file-picker":
          openFilePicker()
          return
        case "open-paint":
          openPaint(effect.tool)
          return
        case "read-clipboard": {
          const generation = ++pasteAttemptGenerationRef.current
          void pasteFromClipboard(navigator.clipboard).then((result) => {
            if (pasteAttemptGenerationRef.current !== generation) return
            if (result.status === "success") {
              acceptPaste(result.image)
            } else {
              setPasteFailure(result.status)
            }
          })
          return
        }
        case "preview-from-input":
          setPreviewBlob(pickPreviewable(upfileRef.current))
          return
        case "set-blob":
          if (
            !setImage(
              effect.tool,
              upfileRef.current,
              effect.image,
              props.allowImageReplies,
            )
          ) {
            dispatchAction("clear-button-clicked")
          }
          return
        case "set-file":
          if (
            !setFile(upfileRef.current, effect.file, props.allowImageReplies)
          ) {
            dispatchAction("clear-button-clicked")
          }
          return
        case "start-hacchan":
          props.form.dispatchEvent(
            new CustomEvent("aimg:hacchan-start", { bubbles: true }),
          )
          return
        case "paint-unavailable":
          console.warn(
            `[upfile-input-v2] ${effect.tool} popup is not configured`,
          )
          return
      }
    }
  }

  return function UpfileInputFragmentV2(props: UpfileInputV2Props): VNode {
    const rootNode = useMemo(
      () => props.componentNode ?? createRoot<TypedEvent>(),
      [props.componentNode],
    )
    useEffect(
      () => () => {
        if (!props.componentNode) rootNode.dispose()
      },
      [props.componentNode, rootNode],
    )
    return (
      <PreactComponentScope node={rootNode}>
        <UpfileInputV2Presenter {...props} />
      </PreactComponentScope>
    )
  }
}

/** v2 upload fragmentのDOMだけを描画するPassive View。 */
export function UpfileInputV2View(props: {
  allowImageReplies: boolean
  controls: UpfileControlState
  previewBlob: File | null
  isPopupFormCollapsed: boolean
  pasteFailure: ClipboardPasteFailure | null
  upfileRef: RefObject<HTMLInputElement>
  baseformRef: RefObject<HTMLInputElement>
  canvasRef: RefObject<HTMLCanvasElement>
  onFileSelected: () => void
  onEvent: (event: UpfileUiInputEvent) => void
}): VNode {
  return (
    <>
      <input
        ref={props.upfileRef}
        type="file"
        name="upfile"
        accept={filePickerAcceptTypes(props.allowImageReplies).join(",")}
        hidden
        onChange={props.onFileSelected}
      />
      <button
        hidden
        id={props.controls.hacchanButton ? "oebtnj" : ""}
        onClick={() =>
          props.onEvent({
            type: "upfile.action.requested",
            action: "hacchan-button-clicked",
          })
        }
        type="button"
      />
      <input
        id={props.controls.baseformInput ? "baseform" : ""}
        type="hidden"
        ref={props.baseformRef}
      />
      <figure
        id="ftbl"
        hidden={!props.controls.oejsCanvas && !props.controls.previewFigure}
        style={{ width: "fit-content", position: "relative" }}
      >
        {props.controls.oejsCanvas && (
          <canvas
            ref={props.canvasRef}
            id={props.isPopupFormCollapsed ? "" : "oejs"}
          />
        )}
        {!props.controls.oejsCanvas &&
          props.controls.previewFigure &&
          props.previewBlob && <PreviewMedia file={props.previewBlob} />}
      </figure>
      {props.pasteFailure && (
        <PasteFailureGuidance
          failure={props.pasteFailure}
          onChooseFile={() =>
            props.onEvent({ type: "upfile.file-picker.requested" })
          }
        />
      )}
    </>
  )
}

/** ページ全体で貼り付け(Ctrl+V)を捕まえる */
export function listenPaste(dispatch: Dispatch<Blob>): () => void {
  const abort = new AbortController()
  document.addEventListener(
    "paste",
    async (e) => {
      const items = e.clipboardData?.items
      const found =
        items &&
        findImage(items, (item, type) => item.type === type && item.getAsFile())
      const blob = await found
      if (blob) {
        dispatch(blob)
      }
    },
    { signal: abort.signal },
  )

  return () => abort.abort()
}

/** fileInputに画像を設定する */
function setImage(
  tool: string,
  fileInput: HTMLInputElement | null,
  image: Blob,
  allowImageReplies: boolean = true,
): boolean {
  if (
    !fileInput ||
    !isAcceptedAttachmentMimeType(image.type, allowImageReplies)
  ) {
    return false
  }

  const file = createTransferredFile(tool, image)
  return setFile(fileInput, file, allowImageReplies)
}

export function createTransferredFile(
  tool: string,
  image: Blob,
  timestamp: number = nowEpochMilliseconds(),
): File {
  const ext = image.type.split("/", 2)[1]
  return markAttachmentSource(
    new File([image], `${tool}_${timestamp}.${ext}`, {
      type: image.type,
    }),
    attachmentSourceForTool(tool),
  )
}

/** 標準MIMEを保ったFileを隠しinputへ設定する */
function setFile(
  fileInput: HTMLInputElement | null,
  file: File,
  allowImageReplies: boolean = true,
): boolean {
  if (
    !fileInput ||
    !isAcceptedAttachmentMimeType(file.type, allowImageReplies)
  ) {
    return false
  }

  const dataTransfer = new DataTransfer()
  dataTransfer.items.add(file)

  fileInput.files = dataTransfer.files
  fileInput.dispatchEvent(new Event("change", { bubbles: true }))
  return true
}

/** フォーム内のファイルDropだけを添付入力へ移す */
export function listenUpfileDrop(
  form: HTMLFormElement,
  dispatch: Dispatch<File>,
  allowImageReplies: boolean = true,
): () => void {
  const abort = new AbortController()
  const opts = { signal: abort.signal }

  form.addEventListener(
    "dragover",
    (event) => {
      if (!hasAcceptedDraggedFile(event.dataTransfer, allowImageReplies)) {
        return
      }
      event.preventDefault()
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy"
      }
    },
    opts,
  )
  form.addEventListener(
    "drop",
    (event) => {
      const file = pickAcceptedDropFile(
        event.dataTransfer?.files,
        allowImageReplies,
      )
      if (!file) {
        return
      }
      event.preventDefault()
      dispatch(file)
    },
    opts,
  )

  return () => abort.abort()
}

export function pickAcceptedDropFile(
  files: ArrayLike<File> | null | undefined,
  allowImageReplies: boolean = true,
): File | undefined {
  if (!files) {
    return
  }
  return Array.from(files).find((file) =>
    isAcceptedAttachmentMimeType(file.type, allowImageReplies),
  )
}

export function hasAcceptedDraggedFile(
  dataTransfer:
    | {
        items?: ArrayLike<{ kind: string; type: string }>
        files?: ArrayLike<File>
      }
    | null
    | undefined,
  allowImageReplies: boolean = true,
): boolean {
  if (!dataTransfer) {
    return false
  }
  const items = Array.from(dataTransfer.items ?? [])
  if (items.length > 0) {
    return items.some(
      (item) =>
        item.kind === "file" &&
        (item.type === "" ||
          isAcceptedAttachmentMimeType(item.type, allowImageReplies)),
    )
  }
  return (
    pickAcceptedDropFile(dataTransfer.files, allowImageReplies) !== undefined
  )
}

export function acceptedAttachmentMimeTypes(
  allowImageReplies: boolean,
): readonly string[] {
  return allowImageReplies
    ? ACCEPTED_ATTACHMENT_MIME_TYPES
    : DRAW_ONLY_ATTACHMENT_MIME_TYPES
}

/** ファイルpickerへ渡す候補。選択後のMIME検査は別途厳密なallowlistで行う。 */
export function filePickerAcceptTypes(
  allowImageReplies: boolean,
): readonly string[] {
  return allowImageReplies
    ? FILE_PICKER_ACCEPT_TYPES
    : DRAW_ONLY_ATTACHMENT_MIME_TYPES
}

/**
 * IANA 未登録の非標準 MIME 別名 → 正式 MIME への写像。
 * サーバー側 `apps/backend/src/attachment/file-name.ts` の `MIME_ALIASES` と揃える。
 */
const MIME_ALIASES: Readonly<Record<string, string>> = {
  "image/jpg": "image/jpeg",
}

/**
 * ブラウザ由来の MIME 文字列を allowlist 照合できる形へ正規化する。
 * `Image/JPEG; charset=binary` → `image/jpeg`、`image/jpg` → `image/jpeg` のように、
 * パラメータ除去、小文字化、非標準別名の吸収を行う。
 * サーバー側 `normalizeMime` と挙動を揃えることで、選択直後の無音破棄を防ぐ。
 */
export function normalizeAttachmentMimeType(type: string): string {
  const base = type.split(";", 1)[0]?.trim().toLowerCase() ?? ""
  return MIME_ALIASES[base] ?? base
}

export function isAcceptedAttachmentMimeType(
  type: string,
  allowImageReplies: boolean = true,
): boolean {
  const normalized = normalizeAttachmentMimeType(type)
  return allowImageReplies
    ? ACCEPTED_ATTACHMENT_MIME_TYPE_SET.has(normalized)
    : DRAW_ONLY_ATTACHMENT_MIME_TYPE_SET.has(normalized)
}

/** 貼付ボタンを押したとき使う。クリップボードから画像を読み出す */
export async function pasteFromClipboard(
  clipboard: Clipboard | undefined,
): Promise<ClipboardPasteResult> {
  if (!clipboard?.read) {
    console.info("Async Clipboard APIを利用できません")
    return { status: "unsupported" }
  }

  try {
    const clipboardItems = await clipboard.read()
    const found = findImage(
      clipboardItems,
      (item, type) => item.types.includes(type) && item.getType(type),
    )
    if (found) {
      return { status: "success", image: await found }
    }
    console.info("クリップボードに画像がありませんでした")
    return { status: "image-not-found" }
  } catch (e) {
    console.warn("クリップボード読み取りエラー:", e)
    return {
      status: isClipboardPermissionError(e)
        ? "permission-denied"
        : "read-error",
    }
  }
}

function isClipboardPermissionError(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return false
  }
  return error.name === "NotAllowedError" || error.name === "SecurityError"
}

/** itemsの中から画像データを探す */
function findImage<T>(
  items: Iterable<T>,
  tryRead: (item: T, type: string) => Blob | Promise<Blob> | null | false,
): Blob | Promise<Blob> | undefined {
  const types = ["image/webp", "image/png", "image/gif", "image/jpeg"]
  for (const item of items) {
    for (const type of types) {
      const blob = tryRead(item, type)
      if (blob) {
        return Promise.resolve(blob)
          .then(tryReencodeWebp)
          .catch(() => blob)
      }
    }
  }
}

/**
 * 選択中ファイルから「プレビュー対象 (画像 or 動画)」だけを抜き出す。
 * それ以外 (PDF等) や未選択は `null` を返す。
 */
function pickPreviewable(input: HTMLInputElement | null): File | null {
  const file = input?.files?.[0]
  if (!file) {
    return null
  }
  if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
    return file
  }
  return null
}

function PasteFailureGuidance({
  failure,
  onChooseFile,
}: {
  failure: ClipboardPasteFailure
  onChooseFile: () => void
}): VNode {
  const title = {
    "permission-denied": "クリップボードの読み取りが許可されていません。",
    "image-not-found": "クリップボードに画像データが見つかりませんでした。",
    unsupported:
      "このブラウザは貼り付けボタンからのクリップボード読み取りに対応していません。",
    "read-error": "クリップボードの読み取り中にエラーが発生しました。",
  }[failure]

  return (
    <section
      role="status"
      aria-live="polite"
      aria-label="添付方法の案内"
      style={pasteGuidanceStyle}
    >
      <strong>{title}</strong>
      {failure === "image-not-found" && (
        <p style={pasteGuidanceParagraphStyle}>
          エクスプローラーなどで画像ファイル自体をコピーした場合、このボタンでは読み取れないことがあります。
        </p>
      )}
      <p style={pasteGuidanceParagraphStyle}>
        この画面で Ctrl+V（Windows/Linux）または
        Cmd+V（Mac）を押すか、「ファイルを選択」またはドラッグ＆ドロップで画像を添付してください。
      </p>
      <button
        type="button"
        onClick={onChooseFile}
        style={pasteGuidanceButtonStyle}
      >
        ファイルを選択
      </button>
    </section>
  )
}

const previewMediaStyle: JSX.CSSProperties = {
  maxWidth: "150px",
  maxHeight: "150px",
  display: "block",
}
const pasteGuidanceStyle: JSX.CSSProperties = {
  display: "block",
  maxWidth: "32rem",
  margin: "0.5rem 0",
  lineHeight: 1.5,
}
const pasteGuidanceParagraphStyle: JSX.CSSProperties = {
  margin: "0.4rem 0 0",
}
const pasteGuidanceButtonStyle: JSX.CSSProperties = {
  display: "block",
  marginTop: "0.5rem",
}
const previewSpacerStyle: JSX.CSSProperties = {
  display: "block",
  height: "1lh",
  width: "1px",
  visibility: "hidden",
  marginTop: "2px",
}
// プレビュー画像/動画の幅に figure を shrink-wrap させ、ボタン等を隣接配置できる
// 意匠を維持するために、ファイル名はテキスト折り返しではなく `displayName` の文字数
// 制限 (20字) によってはみ出しを防いでいる。そのため `whiteSpace: "nowrap"` を維持する。
const previewInfoStyle: JSX.CSSProperties = {
  position: "absolute",
  left: 0,
  top: "calc(100% - 1lh)",
  maxWidth: "100vw",
  color: "#666",
  whiteSpace: "nowrap",
}

/**
 * 選択ファイルのプレビューを figure(id=ftbl) の子として描画する。
 *
 * - `<img>` / `<video>` は通常のブロック子。親 figure は `width: fit-content` で
 *   この要素の幅に shrink-wrap される。
 * - ファイル名・サイズ表示の `<small>` (info) は `position: absolute` で
 *   配置し、figure の幅計算には関与しない (= figure が常に img 幅にフィット)。
 *   `max-width: 100vw` で画面幅に達したら折り返す。
 * - info を absolute にすると縦幅が flow から消えるので、同じ行高の透明な
 *   `<small>` (spacer) を flow に置き、縦幅 1 行分だけ figure 高さに反映する。
 *
 * 親 figure 側で `position: relative` を指定しておく必要がある (要素 JSX 参照)。
 *
 * blob URL は `useMemo` で file 変化時にだけ作り直し、unmount/file 切替時に
 * `useEffect` の cleanup で `revokeObjectURL` する。
 */
function PreviewMedia({ file }: { file: File }): VNode {
  const url = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => {
    return (): void => {
      URL.revokeObjectURL(url)
    }
  }, [url])

  const isVideo = file.type.startsWith("video/")
  const sizeKb = (file.size / 1024).toFixed(1)
  const displayName =
    file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name

  return (
    <>
      {isVideo ? (
        <video src={url} controls muted style={previewMediaStyle} />
      ) : (
        <img src={url} alt="" style={previewMediaStyle} />
      )}
      <small style={previewSpacerStyle} />
      <small style={previewInfoStyle}>
        {displayName} ({sizeKb}KB)
      </small>
    </>
  )
}

function welcomeHacchan(
  canvas: HTMLCanvasElement | null,
  { canvasWidth, canvasHeight }: { canvasWidth: number; canvasHeight: number },
): void {
  if (!canvas) {
    return
  }
  canvas.width = canvasWidth
  canvas.height = canvasHeight
  const c = canvas.getContext("2d")
  if (!c) {
    return
  }

  c.fillStyle = "#f0e0d6"
  c.fillRect(0, 0, canvas.width, canvas.height)
}

/** 投稿フォームが開閉したとき知らせる */
function listenPopupFormToggled(
  form: HTMLFormElement,
  setIsPopupFormCollapsed: (value: boolean) => void,
): () => void {
  const abort = new AbortController()
  form.addEventListener(
    "aimg:popup-form-toggled",
    ({ detail: { isCollapsed } }) => setIsPopupFormCollapsed(isCollapsed),
    { signal: abort.signal },
  )
  return () => abort.abort()
}

/** webpで画像の再圧縮を試みる。小さくならなければ元の画像を返す */
async function tryReencodeWebp(imageBlob: Blob): Promise<Blob> {
  if (imageBlob.type === "image/webp") {
    return imageBlob
  }
  const imageBitmap = await createImageBitmap(imageBlob)
  const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height)
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    return imageBlob
  }
  ctx.drawImage(imageBitmap, 0, 0)
  const webp = await canvas.convertToBlob({ type: "image/webp", quality: 1 })

  return webp.size < imageBlob.size ? webp : imageBlob
}
